// Tracker verticale step intervento — simile al tracking corriere.
// Ogni step ha stato completato/attivo/pendente con animazione.
import { CheckCircle, Circle, Clock, Loader2 } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { formatDate } from '../../../lib/utils'

interface TrackingStep {
  key: string
  label: string
  completed: boolean
  timestamp: string | null
}

interface InterventionTrackerProps {
  steps: TrackingStep[]
  currentStatus: string
  className?: string
}

export function InterventionTracker({
  steps,
  currentStatus,
  className,
}: InterventionTrackerProps) {
  // Individua lo step correntemente attivo (il primo non completato)
  const activeIndex = steps.findIndex((s) => !s.completed)

  return (
    <div
      className={cn('flex flex-col', className)}
      role="list"
      aria-label="Stato avanzamento intervento"
    >
      {steps.map((step, index) => {
        const isActive = index === activeIndex
        const isCompleted = step.completed
        const isLast = index === steps.length - 1

        return (
          <div
            key={step.key}
            className="flex gap-4"
            role="listitem"
            aria-current={isActive ? 'step' : undefined}
          >
            {/* Colonna icona + linea verticale */}
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all',
                  isCompleted && 'border-[var(--fc-success)] bg-[var(--fc-success)] text-white',
                  isActive && 'border-[var(--fc-primary)] bg-white text-[var(--fc-primary)]',
                  !isCompleted && !isActive && 'border-gray-200 bg-white text-gray-300'
                )}
                aria-hidden="true"
              >
                {isCompleted ? (
                  <CheckCircle className="h-5 w-5" />
                ) : isActive ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Circle className="h-5 w-5" />
                )}
              </div>

              {/* Linea verticale tra gli step */}
              {!isLast && (
                <div
                  className={cn(
                    'mt-1 w-0.5 flex-1 transition-colors',
                    isCompleted ? 'bg-[var(--fc-success)]' : 'bg-gray-200'
                  )}
                  style={{ minHeight: '2rem' }}
                  aria-hidden="true"
                />
              )}
            </div>

            {/* Contenuto step */}
            <div className={cn('pb-6', isLast && 'pb-0')}>
              <p
                className={cn(
                  'text-sm font-medium leading-9',
                  isCompleted && 'text-gray-900',
                  isActive && 'text-[var(--fc-primary)]',
                  !isCompleted && !isActive && 'text-gray-400'
                )}
              >
                {step.label}
                {isActive && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-[var(--fc-primary)]">
                    In corso
                  </span>
                )}
              </p>
              {step.timestamp && (
                <p className="flex items-center gap-1 text-xs text-gray-400">
                  <Clock className="h-3 w-3" aria-hidden="true" />
                  {formatDate(step.timestamp)}
                </p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
